// AIPreferencesPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Button,
  TextArea,
  Form,
  InlineLoading,
  Toggle,
  Select,
  SelectItem,
} from '@carbon/react';
import { useNavigate } from 'react-router-dom';

const AIPreferencesPage = ({ token, user }) => {
  const navigate = useNavigate();

  const [yamlText, setYamlText] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [profilesBusy, setProfilesBusy] = useState(false);
  const [autoFallback, setAutoFallback] = useState(false);
  const [message, setMessage] = useState('');

  const [profiles, setProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState('');

  const BASE = '/mlapi/basic/user/preferences/ai';

  const authHeaders = (extras = {}) => ({
    Authorization: `Bearer ${token}`,
    ...extras,
  });

  const showError = (msg) => {
    console.error(msg);
    setError(String(msg));
    setMessage('');
  };

  const showMessage = (msg) => {
    setMessage(String(msg));
    setError('');
  };

  const loadCurrent = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const resp = await fetch(`${BASE}?format=yaml`, {
        headers: authHeaders({ Accept: 'text/plain' }),
      });
      if (!resp.ok) {
        throw new Error(
          `Failed to fetch AI prefs (${resp.status}): ${
            (await resp.text().catch(() => '')) || resp.statusText
          }`,
        );
      }
      const text = await resp.text();
      setYamlText(text);
      showMessage('Loaded current AI preferences.');
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };

  const loadProfiles = async () => {
    setProfilesBusy(true);
    try {
      const resp = await fetch(`${BASE}/profiles`, {
        headers: authHeaders({ Accept: 'application/json' }),
      });
      if (!resp.ok) {
        throw new Error(
          `Failed to fetch AI profiles (${resp.status}): ${
            (await resp.text().catch(() => '')) || resp.statusText
          }`,
        );
      }
      const data = await resp.json();
      setProfiles(data.profiles || []);
      setActiveProfile(data.active_key || '');
    } catch (e) {
      // Don’t nuke the whole page if this fails; just show an error.
      showError(e);
    } finally {
      setProfilesBusy(false);
    }
  };

  useEffect(() => {
    // Load YAML + profiles on mount
    loadCurrent();
    loadProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    setYamlText(e.target.value);
    setError('');
    setMessage('');
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const url = `${BASE}?format=yaml${
        autoFallback ? '&fallback_to_default=true' : ''
      }`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: authHeaders({
          'Content-Type': 'text/plain',
          Accept: 'text/plain',
        }),
        body: yamlText ?? '',
      });
      if (!resp.ok) {
        throw new Error(
          `Save failed (${resp.status}): ${
            (await resp.text().catch(() => '')) || resp.statusText
          }`,
        );
      }
      const serverYaml = await resp.text();
      setYamlText(serverYaml);
      const action = resp.headers.get('X-AI-PREFS-Action');
      showMessage(
        action === 'fallback-default'
          ? 'Saved by restoring default (malformed input).'
          : 'Settings updated successfully!',
      );
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };

  const handleLoadDefaultPreview = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const resp = await fetch(`${BASE}/default?format=yaml`, {
        headers: authHeaders({ Accept: 'text/plain' }),
      });
      if (!resp.ok) {
        throw new Error(
          `Failed to fetch default (${resp.status}): ${
            (await resp.text().catch(() => '')) || resp.statusText
          }`,
        );
      }
      const text = await resp.text();
      setYamlText(text);
      showMessage('Loaded default (not saved yet).');
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };

  const handleRestoreDefaultSave = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const resp = await fetch(`${BASE}/restore-default?format=yaml`, {
        method: 'POST',
        headers: authHeaders({ Accept: 'text/plain' }),
      });
      if (!resp.ok) {
        throw new Error(
          `Restore default failed (${resp.status}): ${
            (await resp.text().catch(() => '')) || resp.statusText
          }`,
        );
      }
      const text = await resp.text();
      setYamlText(text);
      showMessage('Default restored and saved.');
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };

  const handleProfileChange = async (event) => {
    const newKey = event?.target?.value;
    if (!newKey || newKey === activeProfile) return;

    setProfilesBusy(true);
    setError('');
    setMessage('');
    try {
      const resp = await fetch(`${BASE}/profile`, {
        method: 'POST',
        headers: authHeaders({
          'Content-Type': 'application/json',
          Accept: 'application/json',
        }),
        body: JSON.stringify({ key: newKey }),
      });
      if (!resp.ok) {
        throw new Error(
          `Failed to switch profile (${resp.status}): ${
            (await resp.text().catch(() => '')) || resp.statusText
          }`,
        );
      }
      const data = await resp.json();
      setActiveProfile(data.active_key || newKey);

      // Reload YAML so the editor reflects the active profile’s settings
      await loadCurrent();

      const profileMeta = profiles.find((p) => p.key === newKey);
      const label = profileMeta?.label || newKey;
      showMessage(`Switched active profile to "${label}".`);
    } catch (e) {
      showError(e);
    } finally {
      setProfilesBusy(false);
    }
  };

  const handleClose = () => navigate('/');

  const isWorking = busy || profilesBusy;

  return (
    <div className="settings-page">
      <div className="settings-container">
        <h1 className="settings-title">AI Preferences</h1>
        <p className="settings-subtitle">
          Edit your AI configuration in YAML, and choose which profile is active.
          You can preview defaults or restore them.
        </p>

        <div className="settings-tools">
          <div className="settings-row">
            <Toggle
              id="autoFallback"
              labelText="Auto-restore default on malformed save"
              toggled={autoFallback}
              onToggle={(val) => setAutoFallback(val)}
            />
          </div>

          <div className="settings-row">
            <div className="settings-profiles">
              <label htmlFor="aiProfileSelect" className="settings-label">
                Active profile
              </label>
              <Select
                id="aiProfileSelect"
                value={activeProfile || ''}
                onChange={handleProfileChange}
                disabled={profilesBusy || busy || profiles.length === 0}
              >
                {profiles.length === 0 && (
                  <SelectItem
                    value=""
                    text="(no profiles defined)"
                    disabled
                  />
                )}
                {profiles.map((p) => (
                  <SelectItem
                    key={p.key}
                    value={p.key}
                    text={p.label || p.key}
                  />
                ))}
              </Select>
            </div>
          </div>

          {isWorking && <InlineLoading description="Working…" />}
        </div>

        {message && <div className="message-ok">{message}</div>}
        {error && <div className="message-err">{String(error)}</div>}

        <Form onSubmit={handleSave}>
          <div className="form-item">
            <TextArea
              id="aiPrefsYaml"
              labelText="AI Preferences (YAML)"
              value={yamlText}
              onChange={handleChange}
              rows={20}
              className="yaml-editor"
              required
            />
          </div>

          <div className="button-group">
            <Button type="submit" disabled={isWorking}>
              Save
            </Button>
            <Button
              kind="secondary"
              onClick={handleLoadDefaultPreview}
              disabled={isWorking}
            >
              Load Default (preview)
            </Button>
            <Button
              kind="danger"
              onClick={handleRestoreDefaultSave}
              disabled={isWorking}
            >
              Restore Default (save now)
            </Button>
            <Button
              kind="tertiary"
              onClick={loadCurrent}
              disabled={isWorking}
            >
              Reload Current
            </Button>
            <Button kind="tertiary" onClick={handleClose}>
              Close
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AIPreferencesPage;
